import type { Auth0ContextInterface } from '@auth0/auth0-react';
import { differenceInDays } from 'date-fns/differenceInDays';
import { saveAs } from 'file-saver';
import { type CostHistoryResponse, CostHistoryResponseSchema } from '../data/cost-history';
import { type ExceptionLog, ExceptionLogSchema } from '../data/exception-log';
import { type CreateOrder, type Order, OrderSchema } from '../data/order';
import { type OrderItem, OrderItemSchema } from '../data/order-item';
import { type OrderOptions, OrderOptionsSchema } from '../data/order-options';
import { type Product, ProductSchema, saleStat } from '../data/product';
import { type Receipt, ReceiptSchema } from '../data/receipt';
import { type VendorInstructions, VendorInstructionsSchema } from '../data/receiving';
import { type Store, StoreSchema } from '../data/store';
import { type Upload, UploadSchema } from '../data/upload';
import {
  type Companion,
  type UploaderConnection,
  UploaderConnectionSchema,
} from '../data/uploader-connection';
import {
  type Vendor,
  type VendorIntegration,
  VendorIntegrationSchema,
  VendorSchema,
} from '../data/vendor';
import { compare } from '../helpers/column-sort';
import {
  ApiResponseError,
  createMultipleHandler,
  createSingleHandler,
  createZodMultipleHandler,
  createZodSingleHandler,
  FetchWrapper,
  handleVoidResponse,
} from '../helpers/fetch-wrapper';

const baseUrl = `${import.meta.env.VITE_SERVER_URL}/stores`;

// The v2 edit endpoints answer 409 for two unrelated reasons, and the body's
// `code` is what tells them apart — mirrors the constants in
// lambdas/api/api.go. Never infer the cause from which keys the body happens
// to carry: a third 409 added later would be misread as one of these two.
const ORDER_VERSION_CONFLICT_CODE = 'ORDER_VERSION_CONFLICT';
const ORDER_NOT_DRAFT_CODE = 'ORDER_NOT_DRAFT';

// ORDER_VERSION_CONFLICT carries `order`: the server's current order, for the
// client to rebase its pending edits onto (lambdas/api/api_orders.go
// mapOrderOpError). The OTHER 409, ORDER_NOT_DRAFT, carries no payload and
// stays an ordinary ApiResponseError.
export class OrderVersionConflictError extends ApiResponseError {
  latest: Order;

  constructor(error: ApiResponseError, latest: Order) {
    super(error.message, error.status, error.body);
    this.name = 'OrderVersionConflictError';
    this.latest = latest;
  }
}

const orderHandler = createSingleHandler(OrderSchema);

// Response handler for the three v2 order-edit endpoints (batchSetOrderItems,
// allocateOrderDeal, setOrderOptions). All three return the full order, so the
// shared single handler covers success and every ordinary failure; the one
// thing it can't know is that an ORDER_VERSION_CONFLICT 409 is a rebase
// instruction rather than a failure, so that is all this adds on top.
async function handleOrderResponse(response: Response): Promise<Order> {
  try {
    return await orderHandler(response);
  } catch (error) {
    if (
      !(error instanceof ApiResponseError) ||
      error.status !== 409 ||
      error.body?.code !== ORDER_VERSION_CONFLICT_CODE
    ) {
      throw error;
    }

    let latest: Order;
    try {
      latest = await OrderSchema.json().validate(error.body.order);
    } catch {
      // The embedded `order` failed schema validation (backend/frontend
      // schema drift, or a malformed payload) — fall back to the original
      // ApiResponseError rather than letting a raw Yup ValidationError
      // escape. classifyOrderConflict and handleFlushError only know how to
      // recover from ApiResponseError-shaped failures.
      throw error;
    }

    throw new OrderVersionConflictError(error, latest);
  }
}

// The two 409s above are both caller-owned recovery paths rather than failures
// to report, but they recover differently — one rebases onto `latest`, the
// other navigates away. Classifying in one place keeps the generic mutation
// hook's "don't toast this" set and the overlay hook's "here's what to do
// about it" set from drifting apart.
export type OrderConflict = 'version-exhausted' | 'not-draft';

export function classifyOrderConflict(error: unknown): OrderConflict | null {
  // Deliberately keyed on the class, not on ORDER_VERSION_CONFLICT: a conflict
  // whose embedded `order` failed schema validation arrives here as a plain
  // ApiResponseError with no `latest` to rebase onto. Callers suppress the
  // generic toast for every non-null classification, so claiming
  // 'version-exhausted' without a usable order would swallow it silently.
  if (error instanceof OrderVersionConflictError) {
    return 'version-exhausted';
  }

  if (error instanceof ApiResponseError && error.status === 409) {
    // Anything else — an unrecognized code, or none at all — is a 409 we have
    // no recovery for. null means "report it normally", which beats guessing
    // 'not-draft' and bouncing the user off an order they can still edit.
    return error.body?.code === ORDER_NOT_DRAFT_CODE ? 'not-draft' : null;
  }

  return null;
}

export type StoreCreateInput = {
  owner_first_name: string;
  owner_last_name: string;
};

// Mirrors lambdas/api/services.ItemUpdate. `cases`/`units` are nullable
// pointers server-side: null clears the user override so the row falls back to
// the server suggestion, which is a different state from an explicit 0.
export type ItemUpdate = {
  product_id: string;
  cases: number | null;
  units: number | null;
};

export class StoreService {
  fetchWrapper: FetchWrapper;

  constructor(tokenAccessor: Auth0ContextInterface['getAccessTokenSilently']) {
    this.fetchWrapper = new FetchWrapper(tokenAccessor);
  }

  getAll(): Promise<Store[]> {
    return this.fetchWrapper
      .get(baseUrl)
      .then(createMultipleHandler(StoreSchema))
      .then((stores: Store[]) => {
        return stores.sort((s1, s2) => s1.name.localeCompare(s2.name));
      });
  }

  getById(id: string): Promise<Store> {
    return this.fetchWrapper.get(`${baseUrl}/${id}`).then(createSingleHandler(StoreSchema));
  }

  update(id: string, parameters: unknown): Promise<Store> {
    return this.fetchWrapper
      .put(`${baseUrl}/${id}`, parameters)
      .then(createSingleHandler(StoreSchema));
  }

  // vendors

  getVendorsByStore(storeId: string): Promise<Vendor[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/vendors`)
      .then(createMultipleHandler(VendorSchema))
      .then((vendors: Vendor[]) => {
        return vendors.sort((v1, v2) => {
          if (v1.disabled === v2.disabled) {
            return v1.name.localeCompare(v2.name);
          }

          return v1.disabled ? 1 : -1;
        });
      });
  }

  updateVendor(storeId: string, vendor: Vendor): Promise<Vendor> {
    return this.fetchWrapper
      .put(`${baseUrl}/${storeId}/vendors/${vendor.vendor_id}`, vendor)
      .then(createSingleHandler(VendorSchema));
  }

  reEnableRepresentative(storeId: string, vendorId: string, email: string): Promise<Vendor> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/vendors/${vendorId}/representatives/reenable`, { email })
      .then(createSingleHandler(VendorSchema));
  }

  getVendorIntegrations(storeId: string): Promise<VendorIntegration[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/vendor-integrations`)
      .then(createMultipleHandler(VendorIntegrationSchema));
  }

  // products

  getProductsByStore(storeId: string): Promise<Product[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/products`)
      .then(createZodMultipleHandler(ProductSchema))
      .then((products: Product[]) => {
        const today = new Date();

        return products.map((product) => {
          const noStock = product.inventory_qty <= 0;
          const notReceived =
            !product.last_received || differenceInDays(today, product.last_received) >= 365;
          const notSold = saleStat(product, 365) === 0;

          if (noStock && notReceived && notSold) {
            product.auto_disabled = true;
          }

          return product;
        });
      });
  }

  updateProduct(storeId: string, product: Product): Promise<Product> {
    return this.fetchWrapper
      .put(`${baseUrl}/${storeId}/vendors/${product.vendor_id}/products/${product.id}`, product)
      .then(createZodSingleHandler(ProductSchema));
  }

  getProductCostHistory(
    storeId: string,
    vendorId: string,
    productId: string
  ): Promise<CostHistoryResponse> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/vendors/${vendorId}/products/${productId}/cost-history`)
      .then(createSingleHandler(CostHistoryResponseSchema));
  }

  // orders

  getOrdersByStore(storeId: string): Promise<Order[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/orders?status=draft,sending,sent,failed`)
      .then(createMultipleHandler(OrderSchema))
      .then((orders: Order[]) => {
        return orders.sort((o1, o2) => compare(o2.updated_at, o1.updated_at));
      });
  }

  getOrderByStore(storeId: string, orderId: string): Promise<Order> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/orders/${orderId}`)
      .then(createSingleHandler(OrderSchema));
  }

  createOrder(storeId: string, order: CreateOrder): Promise<Order> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/orders`, order)
      .then(createSingleHandler(OrderSchema));
  }

  submitOrder(storeId: string, order: Order, method: string): Promise<Order> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/orders/${order.id}/submit`, {
        order,
        send_method: method,
      })
      .then(createSingleHandler(OrderSchema));
  }

  saveOrderOptions(storeId: string, orderId: string, options: OrderOptions): Promise<OrderOptions> {
    return this.fetchWrapper
      .put(`${baseUrl}/${storeId}/orders/${orderId}/options`, options)
      .then(createSingleHandler(OrderOptionsSchema));
  }

  updateOrderItem(storeId: string, orderId: string, orderItem: OrderItem): Promise<OrderItem> {
    return this.fetchWrapper
      .put(`${baseUrl}/${storeId}/orders/${orderId}/item`, orderItem)
      .then(createSingleHandler(OrderItemSchema));
  }

  // v2 item write: one or more overrides set (or cleared, with null) in a
  // single op — one version bump, one broadcast, no inter-item contention.
  // `cases: null` clears the override and returns the row to its suggestion.
  batchSetOrderItems(storeId: string, orderId: string, updates: ItemUpdate[]): Promise<Order> {
    return this.fetchWrapper
      .patch(`${baseUrl}/${storeId}/orders/${orderId}/items`, { updates })
      .then(handleOrderResponse);
  }

  // The path segment is the product FAMILY id (volume-pricing family), not a
  // deal entity. The server owns the DOH allocation.
  allocateOrderDeal(
    storeId: string,
    orderId: string,
    familyId: string,
    cases: number
  ): Promise<Order> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/orders/${orderId}/deals/${familyId}/allocate`, { cases })
      .then(handleOrderResponse);
  }

  setOrderOptions(storeId: string, orderId: string, options: OrderOptions): Promise<Order> {
    return this.fetchWrapper
      .patch(`${baseUrl}/${storeId}/orders/${orderId}/options`, options)
      .then(handleOrderResponse);
  }

  async downloadOrder(storeId: string, orderId: string, part?: number): Promise<void> {
    const url =
      part !== undefined
        ? `${baseUrl}/${storeId}/orders/${orderId}/download?part=${part}`
        : `${baseUrl}/${storeId}/orders/${orderId}/download`;
    const response = await this.fetchWrapper.get(url);

    if (!response.ok) {
      const json = await response.json();
      throw new Error(`Download failed: ${json?.message || response.statusText}`);
    }

    const disposition = response.headers.get('Content-Disposition');
    const filenameMatch = disposition?.match(/filename="(.+)"/);
    const filename = filenameMatch?.[1] || 'order-download';

    const blob = await response.blob();
    saveAs(blob, filename);
  }

  async deleteOrder(order: Order): Promise<boolean> {
    await this.fetchWrapper.delete(`${baseUrl}/${order.store_id}/orders/${order.id}`);

    return true;
  }

  // uploads

  getUploadsByStore(storeId: string): Promise<Upload[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/uploads`)
      .then(createMultipleHandler(UploadSchema));
  }

  getCompanionConnections(storeId: string): Promise<UploaderConnection[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/companions/connections`)
      .then(createMultipleHandler(UploaderConnectionSchema));
  }

  async getCompanions(storeId: string): Promise<Companion[]> {
    const connections = await this.getCompanionConnections(storeId);

    return connections.filter((c): c is Companion => !!c.uploader);
  }

  triggerUpload(storeId: string): Promise<Upload> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/uploads/trigger`, {})
      .then(createSingleHandler(UploadSchema));
  }

  // exception logs

  getExceptionLogsByStore(storeId: string): Promise<ExceptionLog[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/exception-logs`)
      .then(createMultipleHandler(ExceptionLogSchema))
      .then((logs: ExceptionLog[]) => {
        return logs.sort((a, b) => compare(b.timestamp, a.timestamp));
      });
  }

  // receipts

  getReceipts(storeId: string): Promise<Receipt[]> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/receipts`)
      .then(createMultipleHandler(ReceiptSchema));
  }

  // receiving

  getVendorInstructions(
    storeId: string,
    vendorNormalizedName: string
  ): Promise<VendorInstructions> {
    return this.fetchWrapper
      .get(`${baseUrl}/${storeId}/receiving/instructions/${vendorNormalizedName}`)
      .then(createSingleHandler(VendorInstructionsSchema));
  }

  async claimReceivingSession(
    storeId: string,
    sessionId: string,
    tabId: string
  ): Promise<{ granted: boolean; current_holder?: string }> {
    const response = await this.fetchWrapper.post(
      `${baseUrl}/${storeId}/receiving-sessions/${sessionId}/claim`,
      {},
      { headers: { 'X-Tab-ID': tabId } }
    );
    if (response.status === 409) {
      return response.json();
    }

    await handleVoidResponse(response);

    return response.json();
  }

  generateUploadUrls(
    storeId: string,
    vendorId: string,
    invoices: { filename: string; sha256: string }[]
  ): Promise<{ prefix: string; urls: Record<string, string> }> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/generate-invoices-upload-urls`, {
        vendor_id: vendorId,
        invoices,
      })
      .then((r) => r.json());
  }

  // engagement

  recordEngagement(
    storeId: string,
    eventType: string,
    payload?: Record<string, unknown>
  ): Promise<void> {
    return this.fetchWrapper
      .post(`${baseUrl}/${storeId}/events`, {
        event_type: eventType,
        payload,
      })
      .then(() => undefined);
  }
}
